import * as HighStock from 'highcharts/highstock';
import HSIndicators from 'highcharts/indicators/indicators';
import IndicatorsAll from 'highcharts/indicators/indicators-all';
import Accessibility from 'highcharts/modules/accessibility';
import AnnotationAdvance from 'highcharts/modules/annotations-advanced';
import DragPanes from 'highcharts/modules/drag-panes';
import Exporting from 'highcharts/modules/exporting';
import FullScreen from 'highcharts/modules/full-screen';
import { default as PriceIndicator, default as PriceIndicators } from 'highcharts/modules/price-indicator';
import StockTools from 'highcharts/modules/stock-tools';
import { HighChartAxisBuilder } from '../builders/highchart-axis.builder';
import { HighChartComparisonBuilder } from '../builders/highchart-comparison.builder';
import { HighChartStyleBuilder } from '../builders/highchart-style.builder';
import { HighChartBuilder } from '../builders/highchart.builder';
import { IndicatorHelpers } from '../helpers/indicator-helper';
import { ObjectHelpers } from '../helpers/object-helpers';
import { OptionHelpers } from '../helpers/option-helpers';
import { TIDataPointToToIndiciatorPointMapper } from '../mappers/data-point/ti-chart-point-to-ti-indicator-point.mapper';
import { IChartContainer } from '../models/base/chart-container.interface';
import { IChartDataService } from '../models/base/chart-data-service.interface';
import { ChartStyleSettingsModel } from '../models/chart-style/chart-style-settings.model';
import { RealtimeTradeDataModel } from '../models/data-point/realtime-trade-data.model';
import { TIChartPointModel } from '../models/data-point/ti-chart-point.model';
import { TIComparisonDataModel } from '../models/data-point/ti-comparison-data.model';
import { TIComparisonHistoryDataModel } from '../models/data-point/ti-comparison-history-data.model';
import { TIIndicatorDataModel } from '../models/data-point/ti-indicator-data.model';
import { IndicatorOptionModel } from '../models/drop-down-list/indicator-option.model';
import { IndicatorType } from '../models/enum/indicator-type.enum';
import { ScrollMode } from '../models/enum/scroll-mode.enum';
import { Timeframe } from '../models/enum/timeframe.enum';
import { IndicatorsComponent } from './chart-series/indicators.component';
import { MainChartComponent } from './chart-series/main-chart.component';
import { ComparisonSymbolLegendsComponent } from './comparison-symbol-legends.component';
import { IComponent } from './component.interface';
import { DrawOrderExecutionComponent } from './draw-order-execution.component';
import { DrawTradePriceAnnotationComponent } from './draw-trade-price-annotation.model';
import { IndicatorLastPriceComponent } from './indicators-last-price.component';
import { HighChartIndicatorsComponent } from './menu/highchart-indicators.component';
import { SymbolLookupComponent } from './menu/symbol-lookup.component';
import { TimeframeIconComponent } from './menu/timeframe-icon.component';
import { ToolIconComponent } from './menu/tool-icon.component';
import { TILogoComponent } from './ti-logo.component';
import { TooltipComponent } from './tooltip.component';

declare const window: any

Exporting(HighStock);
Accessibility(HighStock);
DragPanes(HighStock);
HSIndicators(HighStock);
PriceIndicators(HighStock);
IndicatorsAll(HighStock);
AnnotationAdvance(HighStock);
PriceIndicator(HighStock);
FullScreen(HighStock);
StockTools(HighStock);

HighStock.setOptions({
    lang: {
        thousandsSep: ',',
        stockTools: {
            gui: {
                curvedLine: 'Curved Line',
                customLabel: 'Label',
                curvedLineCustomAnnotation: 'Curved Line',
                emojiCustomAnnotation: 'Emoji',
                emojiCustom2Annotation: 'Emoji',
                emoji: 'Emoji',
            }
        },
        navigation: {
            popup: {
                curvedLine: 'Curved Line',
                customLabel: 'Label',
                emoji: 'Emoji',
            }
        }
    }
} as any);

export class TiChartSeriesComponent implements IComponent {
    public id = '';

    public highStockChart: HighStock.Chart = null as any;

    public lastDataPoint: TIChartPointModel | null = null;
    public firstDataPoint: TIChartPointModel | null = null

    private watchExtremesChangeCallbacks: Array<Function> = [];
    private watchRedrawChangeCallbacks: Array<Function> = [];
    private watchHighChartPopupCallbacks: Array<Function> = [];
    private watchAnnotationAddedCallbacks: Array<Function> = [];
    private watchAnnotationAfterUpdatedCallbacks: Array<Function> = [];

    public element: HTMLElement;
    public symbol = '';
    public chartAreaClassName = '.chart';
    public currentTimeframe = Timeframe.OneMinute;
    private chartElement: HTMLElement;

    public ready = false;
    public hasIndicator = false;
    private requestingHistoryData = false;
    private historyDataEnded = false;
    private mouseOver = false;
    private fromMouseWheel = false;
    private autoExtremes = true;

    private readonly maxZoom = 12;
    private minZoom = -10;
    private readonly defaultZoomLevel = 0;

    public originalMinimumXValue = 0;
    public originalMaximumXValue = 0;
    public defaultTotalItemPerScreen = 0;
    public extreamMin = 0;
    public extreamMax = 0;
    public lastIndicatorXValue: number | null = null;
    public originalMinimumXValueWithZoom = 0;
    private originalMinMaxXValueDifferent = 0;
    private zoomPerLevel = 0;
    public currentZoom = 0;

    public base: IChartContainer;
    public highChartAxisBuilder: HighChartAxisBuilder;
    public highChartComparisonBuilder: HighChartComparisonBuilder;
    public chartService: IChartDataService;
    public highChartBuilder: HighChartBuilder;
    public highChartStyleBuilder: HighChartStyleBuilder;

    public chartStyleSettings: ChartStyleSettingsModel | null = null;
    public tiChartPoints: Array<TIChartPointModel> = [];
    public highChartZonePoints: Array<any> = [];
    public tiIndicatorsData: Array<TIIndicatorDataModel> = [];
    private activeIndicators: Array<IndicatorType> = [];
    public highChartIndicators: Array<IndicatorType> = [];
    public tiComparisionsData: Array<TIComparisonDataModel> = [];

    private currentScreen = 0;

    private mainChart: MainChartComponent;
    private indicators: IndicatorsComponent;

    private body = document.body;

    constructor(el: HTMLElement, base: IChartContainer) {
        this.element = el;
        this.base = base;
        this.chartService = base.chartService;
        this.currentScreen = this.element.clientWidth;

        this.highChartBuilder = new HighChartBuilder(this);
        this.highChartStyleBuilder = new HighChartStyleBuilder(this);
        this.highChartAxisBuilder = new HighChartAxisBuilder(this);
        this.highChartComparisonBuilder = new HighChartComparisonBuilder(this);
        this.mainChart = new MainChartComponent(this);
        this.indicators = new IndicatorsComponent(this);
        this.chartElement = this.element.findFirst(this.chartAreaClassName) as HTMLElement;

        if (this.base.marketingWebChartMode) {
            if (this.base.pnpMode) {
                this.minZoom = -24;
            } else {
                this.minZoom = -14;
            }
        } else {
            if (this.base.pnpMode) {
                this.minZoom = -24;
            }
        }

        this.subscribeHistoryScrolling();
        this.subscribeChartDataService();
        this.onBodyClick();
        this.onResize();
        this.detectMouseOver();

        if (this.base.scrollMode == ScrollMode.MouseWheel) {
            this.onMouseWheelHandler();
        } else if (this.base.scrollMode == ScrollMode.ClickAndDrag) {
            this.onClickAndDragHandler();
        }
    }

    public doAutoExtreme() {
        if (!this.autoExtremes) {
            return;
        }

        console.log('Auto extremes');
        const axis = this.highStockChart.xAxis[0];
        if (axis) {
            axis.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue, true, false);
        }
    }

    public addHighChartIndicator(value: IndicatorType) {
        if (this.highChartIndicators.some((x: IndicatorType) => x == value)) {
            return;
        }

        // Need to add volume for highchart Oscillator indicators
        const existingVolumeSeries = this.highStockChart.get('volume');
        if (!existingVolumeSeries) {
            this.highChartIndicators.unshift(IndicatorType.HighChartVolume);
        }

        this.highChartIndicators.push(value);
        console.log('start addHighChartIndicator')
        this.start();
    }

    public removeHighChartIndicator(value: IndicatorType) {
        const index = this.highChartIndicators.findIndex((x: IndicatorType) => x == value);
        if (index < 0) {
            return;
        }

        this.highChartIndicators.splice(index, 1);
        console.log('start removeHighChartIndicator')
        this.start();
    }

    public getAllActiveIndicatorOptions(): Array<IndicatorOptionModel> {
        const result: Array<IndicatorOptionModel> = [];
        const allOptions = OptionHelpers.getIndicatorOptions();

        for (let i = 0; i < this.tiIndicatorsData.length; i++) {
            const indicatorData = this.tiIndicatorsData[i];
            const indicatorType = IndicatorHelpers.getIndicatorType(indicatorData.name);
            const option = allOptions.find((x: IndicatorOptionModel) => x.value == indicatorType);
            // if (option && !result.some((x: IndicatorOptionModel) => x.value == indicatorType)) {
            if (option) {
                const optionToAdd = new IndicatorOptionModel(ObjectHelpers.copy(option));
                optionToAdd.name = indicatorData.name;
                optionToAdd.chartAreaName = indicatorData.chartAreaName;
                result.push(optionToAdd);
            }
        }
        for (let i = 0; i < this.highChartIndicators.length; i++) {
            const indicatorType = this.highChartIndicators[i];
            const option = allOptions.find((x: IndicatorOptionModel) => x.value == indicatorType);
            if (option && !result.some((x: IndicatorOptionModel) => x.value == indicatorType)) {
                result.push(option);
            }
        }

        return result;
    }

    public isCurrentBarVisible(): boolean {
        if (!this.lastDataPoint) {
            return false;
        }

        const xValue = this.lastDataPoint.xValue;
        const mainChartSerie: any = this.highStockChart.series[0];
        return mainChartSerie.processedXData.some((x: number) => x == xValue);
    }

    public isPopupVisible(): boolean {
        const popup = this.element.findFirst('.highcharts-popup:not(.custom-highchart-popup)');
        if (popup && popup.clientHeight > 0) {
            return true;
        }

        const emojiPopup = this.body.findFirst('.add-emoji-control-popup');
        if (emojiPopup && emojiPopup.classList.contains('active')) {
            return true;
        }


        return false;
    }

    public isHoverOnHighChartDrawingTools(event: any): boolean {
        const hasScrollOnElement = !!event.target && !!event.target.closest('.highcharts-stocktools-wrapper');
        if (!!hasScrollOnElement) {
            return true;
        }

        return false;
    }

    private removeAllhighChartIndicator() {
        const allOptions = this.element.querySelectorAll<HTMLElement>('.controls .indicator .tf');
        this.removeActiveElements(allOptions);
    }

    private removeActiveElements(options: NodeListOf<HTMLElement>) {
        options?.forEach((op: HTMLElement) => {
            op.classList.remove('active');
        });
    }

    private detectMouseOver() {
        this.base.domEvents.onMouseOver(() => {
            this.mouseOver = true;
            this.chartService.requestMouseEnter();
        });

        this.base.domEvents.onMouseLeave(() => {
            this.mouseOver = false;
            this.chartService.requestMouseLeave();
        });

        this.element.addEventListener('mousemove', (event: any) => {
            if (!this.highStockChart || !this.highStockChart.pointer) { return; }
            const pointer = this.highStockChart.pointer.normalize(event);
            const yAxis: any = this.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            const toolTip = this.base.getComponentsByType(TooltipComponent)[0] as TooltipComponent;
            const comparisonSymbolLegend = this.base.getComponentsByType(ComparisonSymbolLegendsComponent)[0] as ComparisonSymbolLegendsComponent;
            let yValue = 0;

            if (this.base.pnpMode) {
                yValue = yAxis.toValue(pointer.y - this.element.offsetTop);
            } else {
                yValue = yAxis.toValue(pointer.y);
            }

            if (yValue < yAxis.min) {
                yValue = yAxis.min;
            } else if (yValue > yAxis.max) {
                yValue = yAxis.max;
            }

            toolTip?.setPrice(yValue);
            this.base.chartService.requestUpdateLastPriceClicked(yValue);

            const xAxis: any = this.highStockChart.xAxis[0];
            if (xAxis) {
                const xValue = Math.round(xAxis.toValue(pointer.x, true));
                toolTip?.renderTooltip(xValue + 1);
                comparisonSymbolLegend?.render(xValue + 1);
            }
        });
    }

    private chartReadyTimer: any
    public setChartReady() {
        clearTimeout(this.chartReadyTimer);
        this.chartReadyTimer = setTimeout(() => {
            this.ready = true;
        }, 150);
    }

    private onMouseWheelHandler() {
        this.element.addEventListener('mousewheel', (event: any) => {
            if (!this.mouseOver || !this.highStockChart?.pointer || this.isPopupVisible() || this.isHoverOnHighChartDrawingTools(event) || this.base.isPreview || this.base.disableScrolling) {
                return;
            }

            const ev = this.highStockChart.pointer.normalize(event) as any;
            const doZoom = ev.ctrlKey;
            if (doZoom && this.requestingHistoryData) {
                return;
            }

            let delta = 0;

            delta = ev.detail || -(ev.wheelDelta / 120);
            delta = delta < 0 ? 1 : -1;

            ev.ctrlKey ? this.zoomHandler(delta) : this.scrollHandler(delta);
            this.stopEvent(event);
        }, { passive: false });
    }

    private setExtreamToMinMax() {
        const axis = this.highStockChart.xAxis[0];
        if (axis) {
            axis.setExtremes(this.extreamMin, this.extreamMax, true, false);
        }
    }

    private onClickAndDragHandler() {
        let pos1 = 0, pos2 = 0;
        let dragStart = false;

        if ('ontouchstart' in window || navigator.maxTouchPoints) {
            // This device has touch capabilities
            let shouldZoom = false;
            let initialDistance: number | null = null;
            let zoomimgTimer: any = null;
            const calculateDistance = (touches: TouchList) => {
                const touch1 = touches[0];
                const touch2 = touches[1];
                const dx = touch2.clientX - touch1.clientX;
                const dy = touch2.clientY - touch1.clientY;
                return Math.sqrt(dx * dx + dy * dy);
            }

            this.element.addEventListener("touchstart", (e: TouchEvent) => {
                // Check the number of touches

                dragStart = true;
                shouldZoom = e.touches.length === 2;
                
                if (shouldZoom) {
                    initialDistance = calculateDistance(e.touches);
                }

                pos2 = e.touches[0].clientX;
            });

            this.element.addEventListener('touchmove', (e: TouchEvent) => {
                if (!dragStart) {
                    return;
                }

                pos1 = pos2 - e.touches[0].clientX;

                let differentInPOS1 = Math.abs(pos1);
                if (differentInPOS1 < 10) {
                    return;
                }

                if (shouldZoom) {
                    const currentDistance = calculateDistance(e.touches);
                    let deltaZoom = 1;

                    if (currentDistance > initialDistance) {
                        // Pinch out (zoom out)
                        deltaZoom = -1;
                    }

                    // alert('shouldZoom ' + delta)
                    clearTimeout(zoomimgTimer);
                    zoomimgTimer = setTimeout(() => {
                        this.setExtreamToMinMax();
                        this.zoomHandler(deltaZoom);
                        // alert('setExtreamToMinMax ')
                    }, 200);
                    //this.zoomHandler(delta);
                } else {
                    pos2 = e.touches[0].clientX;

                    let delta = pos1 / 100;
                    delta = delta < 0 ? -1 : 1;

                    this.scrollHandler(delta);
                }
            });
            this.element.addEventListener('touchend', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });

            this.element.addEventListener('touchcancel', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });
        } else {
            this.element.addEventListener('mousedown', (e: MouseEvent) => {
                if (dragStart) {
                    return;
                }

                dragStart = true;
                pos2 = e.clientX;
            });

            this.body.addEventListener('mousemove', (e: MouseEvent) => {
                if (!dragStart) {
                    return;
                }

                pos1 = pos2 - e.clientX;
                pos2 = e.clientX;

                let delta = pos1 / 100;
                delta = delta < 0 ? -1 : 1;

                const shouldZoom = e.ctrlKey;

                if (shouldZoom) {
                    this.zoomHandler(delta);
                } else {
                    this.scrollHandler(delta);
                }
            });

            this.body.addEventListener('mouseleave', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });

            this.body.addEventListener('mouseup', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });
        }
    }

    private scrollHandler(delta: number) {
        const axis = this.highStockChart.xAxis[0];
        if (!axis) {
            return;
        }

        const extremes = axis.getExtremes();
        const step = (extremes.max - extremes.min) / 8 * delta;
        let max = extremes.max + step;
        let min = extremes.min + step;
        // console.log('max ', max, 'min ', min)
        if (max > this.originalMaximumXValue) {
            this.autoExtremes = true;
            // console.log('UnPause update ');
            axis.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue, true, false);
            return;
        } else {
            // console.log('Pause update ');
            this.autoExtremes = false;
        }

        const series: any = this.highStockChart.series[0];
        const firstVisibleBar = series?.processedXData[0];
        const oldestData = series?.xData[0];
        if (delta == -1 && oldestData != null && (this.requestingHistoryData || firstVisibleBar < oldestData || (this.historyDataEnded && min < oldestData))) {
            console.log('End of history', min, oldestData)
            return;
        }

        this.extreamMin = min;
        this.extreamMax = max;

        if (delta == -1) {
            this.fromMouseWheel = true;
        }

        axis.setExtremes(this.extreamMin, this.extreamMax, true, false);
    }

    private zoomHandler(delta: number) {
        if (delta > 0) {
            if (this.currentZoom >= this.maxZoom) {
                console.log('zoom reach max');
                return;
            }

            this.currentZoom++;
        } else if (delta < 0) {
            if (this.currentZoom <= this.minZoom) {
                console.log('zoom reach min');
                return;
            }

            this.currentZoom--;
        } else {
            return;
        }

        // this.base.hideTooltip();
        const axis = this.highStockChart.xAxis[0];
        if (!axis) {
            return;
        }

        const extremes = axis.getExtremes();

        this.calculateZoomPerLevel();

        this.extreamMax = extremes.max;
        this.calcExtremMinFromZoom();

        console.log('current zoom : ', this.currentZoom, 'extreamMin : ', this.extreamMin, 'extreamMax : ', this.extreamMax)
        this.fromMouseWheel = true;
        axis.setExtremes(this.extreamMin, this.extreamMax, true, false);
        // setTimeout(() => {
        // this.highChartAxisBuilder.buildAxisComponents();
        // this.highChartComparisonBuilder.buildComparisonsComponents(0, false);
        // }, 150);

        if (this.chartStyleSettings) {
            this.chartStyleSettings.zoomLevel = this.currentZoom;
            this.base.chartService.requestSetZoomLevel(this.currentZoom);
        }
    }

    private historyLoadTimmer: any = null;
    private subscribeHistoryScrolling() {
        this.subscribeExtremeChange(() => {
            setTimeout(() => this.setChartComponentStyles(), 150);

            const series: any = this.highStockChart.series[0];
            const firstVisibleBar = series?.processedXData[0];
            const oldestData = series?.xData[0];
            this.setChartReady();
            if (this.historyDataEnded || this.base.isPreview) {
                return;
            }

            if (firstVisibleBar - 10 < oldestData && !this.requestingHistoryData && this.fromMouseWheel) {
                this.requestingHistoryData = true;
                clearTimeout(this.historyLoadTimmer);
                this.historyLoadTimmer = setTimeout(() => {
                    console.log('this.requestingHistoryData = true');
                    this.ready = false;
                    this.fromMouseWheel = false;
                    this.chartService.requestHistory();
                }, 100);
            }
        });
    }

    private calculateZoomPerLevel() {
        if (this.base.marketingWebChartMode) {
            const ZOOMING_IN_RANGE_ONE = -3;
            const ZOOMING_IN_RANGE_TWO = this.base.pnpMode ? -12 : -6;
            if (this.currentZoom > this.defaultZoomLevel) {
                // Zooming in, past default chart size.
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 12;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_ONE) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 6;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_TWO) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2.5;
            } else {
                if (this.base.pnpMode) {
                    this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.6;
                } else {
                    const ZOOMING_IN_RANGE_THREE = -8;
                    const ZOOMING_IN_RANGE_FOUR = -9;
                    const ZOOMING_IN_RANGE_FIVE = -10;
                    if (this.currentZoom > ZOOMING_IN_RANGE_THREE) {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2.5;
                    } else if (this.currentZoom > ZOOMING_IN_RANGE_FOUR) {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2;
                    } else if (this.currentZoom > ZOOMING_IN_RANGE_FIVE) {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.3;
                    } else {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.6;
                    }
                }
            }
        } else {
            const ZOOMING_IN_RANGE_ONE = -3;
            const ZOOMING_IN_RANGE_TWO = -6;
            if (this.currentZoom > this.defaultZoomLevel) {
                // Zooming in, past default chart size.
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 12;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_ONE) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 6;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_TWO) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 3;
            } else {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.6;
            }
        }
    }

    public calcExtremMinFromZoom() {
        if (this.base.isPreview) {
            return;
        }

        // console.log(this.extreamMax, this.defaultTotalItemPerScreen, this.zoomPerLevel, this.currentZoom)
        this.extreamMin = (this.extreamMax - this.defaultTotalItemPerScreen) + (this.zoomPerLevel * this.currentZoom);
        this.originalMinimumXValueWithZoom = (this.originalMaximumXValue - this.defaultTotalItemPerScreen) + (this.zoomPerLevel * this.currentZoom);
    }

    public calcMinMaxTimeframeFromXValue(xValue: number, updateOriginalDiff?: boolean) {
        if (this.base.isPreview) {
            if (this.tiChartPoints?.length) {
                this.originalMinimumXValue = this.tiChartPoints[0].xValue;
                this.originalMaximumXValue = this.tiChartPoints[this.tiChartPoints.length - 1].xValue;
            }

            return;
        }

        this.defaultTotalItemPerScreen = this.getDefaultTotalXValuePerScreen();
        this.originalMaximumXValue = xValue;
        this.originalMinimumXValue = xValue - this.defaultTotalItemPerScreen;

        this.originalMinimumXValueWithZoom = (this.originalMaximumXValue - this.defaultTotalItemPerScreen) + (this.zoomPerLevel * this.currentZoom);

        if (updateOriginalDiff) {
            this.originalMinMaxXValueDifferent = this.originalMaximumXValue - this.originalMinimumXValue;
        }
    }

    private stopEvent(event: any) {
        if (!event) {
            return;
        }

        if (event.preventDefault) {
            event.preventDefault();
        }

        if (event.stopPropagation) {
            event.stopPropagation();
        }

        event.cancelBubble = true;
    }

    private subscribeChartDataService() {
        this.chartService.chartReady();
        this.subscribeChartStyleData();
        this.subscribeChartDataInit();
        this.subscribeChartComparisionsDataInit();
        this.subscribeChartIndicatorsDataInit();
        this.subscribeRealtimeData();
        this.subscribe10sRefreshChartData();
        this.subscribe10sRefreshIndicatorsData();
        this.subscribeUpdateChartHistoryData();
        this.subscribeUpdateChartComparisonHistoryData();
        this.subscribeUpdateHistoryIndicatorsData();
        this.subscribeWindowMinimizeUpdate();
    }

    public hasActiveIndicator(indicator: IndicatorType): boolean {
        return this.activeIndicators.indexOf(indicator) > -1;
    }

    public setPanel() {
        const tilogo = this.base.getComponentsByType(TILogoComponent)[0] as TILogoComponent;
        tilogo?.show(this.currentTimeframe);

        const symbolLookup = this.base.getComponentsByType(SymbolLookupComponent)[0] as SymbolLookupComponent;
        symbolLookup?.setSymbol(this.symbol);

        const timeframe = this.base.getComponentsByType(TimeframeIconComponent)[0] as TimeframeIconComponent;
        timeframe?.setCurrentTimeframe(this.currentTimeframe);
    }

    public hasAnyComparisonSymbols(): boolean {
        return this.tiComparisionsData?.length > 0;
    }

    private updateChartStyleDataTimer: any;
    private subscribeChartStyleData() {
        this.chartService.subscribeChartStyleData((chartStyleSettings: ChartStyleSettingsModel) => {
            clearTimeout(this.updateChartStyleDataTimer);
            this.updateChartStyleDataTimer = setTimeout(() => {

                window.thousandSeparator = chartStyleSettings.thousandSeparator;
                window.decimalSeparator = chartStyleSettings.decimalSeparator;

                this.highChartStyleBuilder.createCrosshairsStyle(chartStyleSettings);
                this.highChartStyleBuilder.createGridLinesStyle(chartStyleSettings);
                this.highChartStyleBuilder.createPlotbandsStyle(chartStyleSettings);

                if ((this.chartStyleSettings && this.chartStyleSettings.equals(chartStyleSettings))) {
                    this.highChartAxisBuilder.buildAxisComponents();
                    return;
                }

                this.chartStyleSettings = chartStyleSettings;
                if (this.chartStyleSettings?.isDarkTheme) {
                    this.element.addClass('dark-theme');
                } else {
                    this.element.removeClass('dark-theme');
                }
                if (this.tiChartPoints?.length) {
                    console.log('start subscribeChartStyleData')
                    this.start();
                    setTimeout(() => {
                        this.highChartComparisonBuilder.buildComparisonsComponents(0, true);
                    }, 150);
                }
            }, 100);
        });
    }

    private subscribeChartDataInit() {
        this.chartService.subscribeInitChartData((data: Array<TIChartPointModel>, timeframe: Timeframe, symbol: string) => {
            // this.base.setComponentInitialState();

            this.historyDataEnded = false;
            this.requestingHistoryData = false;

            this.symbol = symbol;
            this.currentTimeframe = timeframe;
            this.removeAllhighChartIndicator();
            this.tiChartPoints = data;
        });
    }

    private subscribeChartComparisionsDataInit() {
        this.chartService.subscribeInitChartComparisonsData((data: Array<TIComparisonDataModel>) => {
            this.tiComparisionsData = data;
            this.highChartComparisonBuilder.buildComparisonsComponents(0, true);
        });
    }

    // private chartIndicatorsDataInitTimer: any;
    private subscribeChartIndicatorsDataInit() {
        this.chartService.subscribeInitChartIndicatorsData((data: Array<TIIndicatorDataModel>) => {
            // console.log('subscribeInitChartIndicatorsData  -- : ' , data);
            // console.log('subscribeInitChartIndicatorsData  isPNP -- : ' , this.base.pnpMode);
            // clearTimeout(this.chartIndicatorsDataInitTimer);
            // this.chartIndicatorsDataInitTimer = setTimeout(() => {
            this.activeIndicators = [];
            this.addIndicatorsData(data);
            const indicatorOptions = this.getAllActiveIndicatorOptions();
            this.toggleIndicators(indicatorOptions);
            // console.log('start subscribeInitChartIndicatorsData')
            this.start();
            // }, 15);
        });
    }

    private addIndicatorsData(initiailizeData: Array<TIIndicatorDataModel>) {
        if (!initiailizeData?.length) {
            this.tiIndicatorsData = [];
            this.hasIndicator = false;
            return;
        }

        this.hasIndicator = true;
        this.tiIndicatorsData = TIDataPointToToIndiciatorPointMapper.map(initiailizeData, this.tiChartPoints);
    }

    public hideWithOpacity() {
        // this.chartElement.style.opacity = 0;
        this.element.querySelectorAll<HTMLElement>('.highcharts-series-group').forEach((el: HTMLElement) => {
            // el.style.opacity = 0;
            el.style.visibility = 'hidden';
        });
        this.element.querySelectorAll<HTMLElement>('.highcharts-plot-band').forEach((el: HTMLElement) => {
            el.style.visibility = 'hidden';
        });
    }

    public showWithOpacity() {
        // this.chartElement.style.opacity = '';
        this.element.querySelectorAll<HTMLElement>('.highcharts-series-group').forEach((el: HTMLElement) => {
            // el.style.opacity = '';
            el.style.visibility = '';
        });
        this.element.querySelectorAll<HTMLElement>('.highcharts-plot-band').forEach((el: HTMLElement) => {
            el.style.visibility = '';
        });
    }

    private afterStartTimer: any;
    public start() {
        if (!this.chartStyleSettings) {
            return;
        }

        if (this.highStockChart?.destroy) {
            this.highStockChart.destroy();
            this.highStockChart = null;
        }

        this.ready = false;
        this.base.hideTooltip();
        const highChartConfig = this.highChartBuilder.getConfig();
        this.lastDataPoint = this.tiChartPoints[this.tiChartPoints.length - 1];
        if (!this.lastDataPoint) {
            return;
        }

        this.calcMinMaxTimeframeFromXValue(this.lastDataPoint.xValue, true);
        console.log('create HighStock.stockChart ', highChartConfig, this.chartStyleSettings, ' is pnp: ', this.base.pnpMode);

        this.setZoomLevel();

        this.highChartStyleBuilder.createMainChartStyle(this.chartStyleSettings);
        this.highChartStyleBuilder.createIndicatorStyle(this.tiIndicatorsData);

        this.highStockChart = HighStock.stockChart(this.chartElement, highChartConfig);

        const series: any = highChartConfig?.series;
        this.highChartZonePoints = series && series[0] ? (series.zones ?? []) : [];

        this.autoExtremes = true;
        this.extreamMin = this.originalMinimumXValueWithZoom;
        this.extreamMax = this.originalMaximumXValue;
        this.firstDataPoint = this.tiChartPoints[0];
        console.log('create this.chart ', this.highStockChart);

        // this.base.drawingToolsManager?.sync();

        if (this.tiIndicatorsData?.length) {
            this.lastIndicatorXValue = this.tiIndicatorsData[0].points?.length ? this.tiIndicatorsData[0].points[this.tiIndicatorsData[0].points.length - 1].xValue : null;
        }

        const indicatorLastPriceComponent = this.base.getComponentsByType(IndicatorLastPriceComponent)[0] as IndicatorLastPriceComponent;
        indicatorLastPriceComponent?.createLastPriceForIndicators();

        const drawOrderExecutionComponent = this.base.getComponentsByType(DrawOrderExecutionComponent)[0] as DrawOrderExecutionComponent;
        drawOrderExecutionComponent?.createElement();

        const drawTradePriceAnnotationComponent = this.base.getComponentsByType(DrawTradePriceAnnotationComponent)[0] as DrawTradePriceAnnotationComponent;
        drawTradePriceAnnotationComponent?.createElement();

        if (this.currentZoom != 0) {
            this.calculateZoomPerLevel();

            this.calcExtremMinFromZoom();
        }

        this.showWithOpacity();
        this.doAutoExtreme();
        this.setChartReady();
        this.setChartComponentStyles();
        this.setPanel();

        clearTimeout(this.afterStartTimer);
        this.afterStartTimer = setTimeout(() => {
            const toolIconComponent = this.base.getComponentsByType(ToolIconComponent)[0] as ToolIconComponent;
            toolIconComponent?.addToolIconComponents(this.chartStyleSettings?.startDrawingToolsExpanded ?? false);

            const highChartIndicatorsComponent = this.base.getComponentsByType(HighChartIndicatorsComponent)[0] as HighChartIndicatorsComponent;
            highChartIndicatorsComponent?.toggle(this.chartStyleSettings?.highChartIndicatorsEnabled ?? false);

            this.highChartAxisBuilder.buildAxisComponents();
            
            this.base.drawingToolsManager?.sync();
        }, 500);
    }

    public setZoomLevel() {
        this.currentZoom = this.chartStyleSettings?.zoomLevel ?? 0;
        // console.log('setZoomLevel this.currentZoom', this.currentZoom)
    }

    public setChartComponentStyles() {
        this.base.setLastPricePositions();
    }

    public redrawHighChartIndicator() {
        const allIndicatorOptions = this.getAllActiveIndicatorOptions().filter((x: IndicatorOptionModel) => x.highChartIndicator);
        for (const indicatorOption of allIndicatorOptions) {
            this.removeHighChartIndicator(indicatorOption.value);
            this.addHighChartIndicator(indicatorOption.value);
        }
    }

    private subscribe10sRefreshChartData() {
        this.chartService.subscribeUpdate10sRefreshChartData((data: Array<TIChartPointModel>) => {
            if (data?.length <= 0 || this.requestingHistoryData) {
                return;
            }

            for (const point of data) {
                console.log('Start update point , updateChartData', point)
                this.mainChart.updateChartData(point);
            }

            this.base.setLastPricePositions();
        });
    }

    private subscribe10sRefreshIndicatorsData() {
        this.chartService.subscribeUpdate10sRefreshIndicatorsData((data: Array<TIIndicatorDataModel>) => {
            // setTimeout(() => {
            //     this.highChartAxisBuilder.buildAxisComponents();
            // }, 100);

            if (!data?.length || this.requestingHistoryData) {
                return;
            }

            this.indicators.updateIndicatorsData(data);
            this.base.setLastPricePositions();
        });
    }

    private subscribeUpdateChartHistoryData() {
        this.chartService.subscribeUpdateChartHistoryData((data: Array<TIChartPointModel>) => {
            if (!data?.length) {
                this.historyDataEnded = true;
                return;
            }

            if (this.base.marketingWebChartMode) {
                // console.log('History data marketingWebChartMode ', this.firstDataPoint, data);
                if (data?.length == 1) {
                    this.historyDataEnded = true;
                    return;
                }
            }

            console.log('History data');
            this.mainChart.updateHistoryChartData(data);
        });
    }

    private subscribeUpdateChartComparisonHistoryData() {
        this.chartService.subscribeUpdateChartHistoryComparisonData((data: TIComparisonHistoryDataModel) => {
            this.highChartComparisonBuilder.updateComparisonHistoryData(data);
        });
    }

    private subscribeUpdateHistoryIndicatorsData() {
        this.chartService.subscribeUpdateHistoryIndicatorsData((data: Array<TIIndicatorDataModel>) => {
            setTimeout(() => {
                setTimeout(() => {
                    console.log('this.requestingHistoryData = false');
                    // this.highChartAxisBuilder.buildAxisComponents();
                    this.requestingHistoryData = false;
                }, 100);

                this.indicators.updateHistoryChartIndicatorData(data);
            }, 50);
        });
    }

    private subscribeWindowMinimizeUpdate() {
        this.chartService.subscribeWindowMinimizeUpdate((shouldRedraw: boolean) => {
            if (shouldRedraw) {
                // console.log(' subscribeWindowMinimizeUpdate reflow ', 'width ', document.body.clientWidth, ' height ', document.body.clientHeight)

                // this.doAutoExtreme();
                this.highStockChart?.reflow();
                this.highStockChart?.redraw();
            }
        });
    }

    public toggleIndicators(indicatorOptions: Array<IndicatorOptionModel>) {
        // add indicators
        for (const indicatorOption of indicatorOptions) {
            if (!this.hasActiveIndicator(indicatorOption.value)) {
                this.activeIndicators.push(indicatorOption.value);
            }
        }
    }

    private subscribeRealtimeData() {
        this.chartService.subscribeRealtimeData((data: RealtimeTradeDataModel) => this.whenGetNewTradeData(data))
    }

    private whenGetNewTradeData(tradeData: RealtimeTradeDataModel) {
        if (!this.ready || this.requestingHistoryData || tradeData.symbol != this.symbol) {
            return;
        }

        this.mainChart.updateRealTimeChartData(tradeData);
    }

    public onExtremeChange(ev: any) {
        this.watchExtremesChangeCallbacks.forEach((fx: Function) => {
            fx(ev);
        });
    }

    public subscribeExtremeChange(fx: Function) {
        this.watchExtremesChangeCallbacks.push(fx);
    }

    public onRedraw() {
        this.watchRedrawChangeCallbacks.forEach((fx: Function) => {
            fx();
        });
    }

    public subscribeRedrawChange(fx: Function) {
        this.watchRedrawChangeCallbacks.push(fx);
    }

    public onPopupChange(open: boolean, event: any) {
        this.watchHighChartPopupCallbacks.forEach((fx: Function) => {
            fx(open, event);
        });
    }

    public subscribePopupChange(fx: Function) {
        this.watchHighChartPopupCallbacks.push(fx);
    }

    public onAnnotationAdd(event: any, fromCache: boolean) {
        this.watchAnnotationAddedCallbacks.forEach((fx: Function) => {
            fx(event, fromCache);
        });
    }

    public subscribeAnotationAdd(fx: Function) {
        this.watchAnnotationAddedCallbacks.push(fx);
    }

    public onAnnotationAfterUpdate(event: any) {
        this.watchAnnotationAfterUpdatedCallbacks.forEach((fx: Function) => {
            fx(event);
        });
    }

    public subscribeAnotationAfterUpdate(fx: Function) {
        this.watchAnnotationAfterUpdatedCallbacks.push(fx);
    }

    private getDefaultTotalXValuePerScreen(): number {
        if (this.base.marketingWebChartMode) {
            if (this.element.clientWidth <= 350) {
                return 20;
            }
            
            return 40;
        }

        if (this.element.clientWidth <= 450)
            return 20;
        else if (this.element.clientWidth <= 640)
            return 40;
        else if (this.element.clientWidth <= 830)
            return 60;

        return 100;
    }

    private onBodyClick() {
        this.base.domEvents.onScreenClick(() => {
            this.chartService.notifyChartHideContextMenu();
        });
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(() => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                if (this.base.isPreview) {
                    return;
                }
    
                this.afterResize();
            }, 250);
        });
    }

    public afterResize() {
        this.setChartComponentStyles();
        setTimeout(() => {
            this.adjustBarAfterResize();
            this.highChartAxisBuilder.buildAxisComponents();
        }, 100);
    }

    private adjustBarAfterResize() {
        const axis = this.highStockChart?.xAxis[0];
        if (!axis) {
            return;
        }

        // console.log('adjustBarAfterResize' , this.currentScreen, this.element.clientWidth, this.base.marketingWebChartMode)
        if (this.currentScreen == this.element.clientWidth && !this.base.marketingWebChartMode) {
            return;
        }

        this.currentScreen = this.element.clientWidth;
        this.calcMinMaxTimeframeFromXValue(this.originalMaximumXValue, true);
        axis.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue, true, false);
        this.highStockChart.reflow();
    }
}
